<template>
  <settings-form :breadcrumbs="useSettingsBreadcrumbs(service.title || $t('label.new_service'))">
    <template #actions>
      <v-btn
        v-if="IS_SVCMGR_INTERNAL_BUILD"
        color="warning black--text"
        @click="serviceMergeDialogVisible=true"
      >
        <v-icon>mdi-merge</v-icon>
        Merge
      </v-btn>
      <v-btn
        class="mr-2"
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>
        {{ $t('button.save_changes') }}
      </v-btn>
      <v-btn
        class="mr-2"
        color="error"
        :disabled="isNew"
        @click="deleteConfirmation"
      >
        <v-icon>mdi-delete</v-icon>
        <span class="d-none d-lg-block">{{ $t('button.delete') }}</span>
      </v-btn>
    </template>
    <template #content>
      <service-merge-dialog
        v-if="serviceMergeDialogVisible"
        :parent-service-id="service.id"
        @close="closeServiceMergeDialog"
      />
      <image-upload-dialog
        v-if="uploadDialog"
        :title="$t('title.image')"
        :image-src="serviceImageUrl"
        filename-prefix="svc"
        :help-label="$t('placeholder.image_upload')"
        :delete-confirmation-label="$t('message.confirm_delete_uploaded_image')"
        :stencil-props="{ aspectRatio: 686/450 }"
        :max-width="686"
        @close="uploadDialog = false"
        @change="uploadImage"
      />
      <validation-observer
        ref="refObserver"
      >
        <settings-group
          gutter
          :gutter-md="12"
          :gutter-lg="10"
          :gutter-xl="10"
          :title="$t('settings.service_manager.appearance_title')"
          :subtitle="$t('settings.service_manager.appearance_subtitle')"
        >
          <multi-language-group
            v-model="selectedLocale"
            :enabled-locales="enabledLocales"
            :translated-locales="translatedLocales"
            :error-locales="tInError"
            enable-removal
            @add="addTranslation"
            @delete="deleteTranslation"
          >
            <v-col
              cols="12"
              class="pb-0 my-2 d-flex"
            >
              <validated-text-field
                v-model="serviceTitle"
                class="ml-2 pt-0 w-100"
                autofocus
                persistent-hint
                :hint="$t('label.service_title_hint')"
                :label="$t('label.service_title')"
                focus-on-mount
              />
            </v-col>
            <v-col
              class="py-0 my-0 mt-2"
              cols="12"
            >
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <input
                  v-model="serviceImageUrl"
                  type="hidden"
                >
                <image-preview-field
                  :image-url="serviceImageUrl"
                  :title="$t('label.service_manager_image')"
                  :placeholder="$t('placeholder.upload_service_image')"
                  :errors="errors"
                  @upload:click="uploadDialog = true"
                />
              </validation-provider>
            </v-col>
            <v-col
              class="py-0 my-0"
              cols="0"
              lg="6"
            />

            <v-col
              class="py-0 my-0 mt-2"
              cols="12"
            >
              <validation-provider
                v-slot="{ errors }"
                rules="required|max:1500"
              >
                <rich-text-editor
                  v-model="serviceDescription"
                  dense
                  richtext-allowed
                  encode-linebreak
                  :label="$t('label.description')"
                  :error-messages="errors"
                  height="224px"
                />
              </validation-provider>
            </v-col>

            <!-- tags -->
            <template v-for="(n, i) in [1,2,3]">
              <v-col
                :key="`tag_${i}`"
                cols="4"
                class="py-0 mt-3"
              >
                <validated-text-field
                  v-model="serviceTags[i]"
                  rules="max:255"
                  class="py-1"
                  :label="`${$t('label.tag')} ${n}`"
                />
              </v-col>
            </template>
          </multi-language-group>

          <v-col
            class="py-0 my-0 mt-8"
            cols="12"
          >
            <v-switch
              v-model="service.isActive"
              :label="$t('label.active')"
              class="py-0 mt-0"
              hide-details
            />
            <p class="pt-0 mt-0 ml-12">
              {{ $t('label.help_service_is_active') }}
            </p>
          </v-col>
          <v-col
            cols="12"
            class="py-0 py-0 mt-1"
          >
            <v-switch
              v-model="service.isPromoted"
              :label="$t('label.front_page')"
              class="py-0 mt-0"
              hide-details
            />
            <p class="pt-0 mt-0 ml-12">
              {{ $t('label.help_service_promoted') }}
            </p>
          </v-col>
          <v-col
            cols="12"
            class="py-0 my-0 mt-1"
          >
            <v-switch
              v-model="service.isRequired"
              :label="$t('label.required')"
              class="py-0 mt-0"
              :hint="$t('label.')"
              hide-details
            />
            <p class="pt-0 mt-0 ml-12">
              {{ $t('label.help_service_required') }}
            </p>
          </v-col>
          <v-col
            cols="12"
            lg="6"
            class="pt-0 pt-lg-2"
          >
            <color-picker-field
              v-model="service.color"
              :label="$t('label.color')"
              :hint="$t('label.service_color_hint')"
              show-swatches
              persistent-hint
              :swatches="fullSwatchPalette"
            />
          </v-col>
          <v-col
            v-if="$vuetify.breakpoint.lgAndUp"
            cols="6"
          />
          <!-- Service Code -->
          <v-col
            v-if="service.code"
            cols="12"
            lg="6"
            class="d-flex pt-3 pt-lg-2 pb-0"
          >
            <v-text-field
              v-model="service.code"
              class="pt-0 readonly--disabled"
              :label="$t('label.service_code')"
              readonly
            />
            <v-btn
              small
              icon
              @click="copySvcCode"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </v-col>
          <!-- / -->
        </settings-group>
        <!--  -->
        <settings-group
          :title="$t('settings.service_manager.service_availability_title')"
          :subtitle="$t('settings.service_manager.service_availability_subtitle')"
          gutter
        >
          <v-col
            class="py-0 my-0"
            cols="12"
            lg="6"
          >
            <validation-provider
              v-slot="{ errors }"
              name="vpFromTime"
              rules="required|gteqTimeValue:06:00|lteqTimeValue:23:45|lteqTime:@vpToTime"
            >
              <time-picker-field
                v-model="service.localStartTime"
                :label="$t('label.service_time_from')"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <v-col
            class="py-0 my-0"
            cols="12"
            lg="6"
          >
            <validation-provider
              v-slot="{ errors }"
              name="vpToTime"
              rules="required|gteqTimeValue:06:00|lteqTimeValue:23:45|gteqTime:@vpFromTime"
            >
              <time-picker-field
                v-model="service.localEndTime"
                :label="$t('label.service_time_until')"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <v-col
            cols="12"
            class="py-0"
          >
            <span class="subheading v-label">{{ $t('label.weekdays') }}</span>
            <v-chip-group
              v-model="service.weekDays"
              class="pt-0 mt-0"
              active-class="chip-prominent-active"
              column
              color="green"
              multiple
              @change="weekDaysChanged"
            >
              <v-chip
                v-for="(w, i) in localized.weekdays()"
                :key="i"
                class="pt-0 mt-0"
                small
                :value="w.value === 0 ? 7: w.value"
                label
              >
                {{ w.text }}
              </v-chip>
              <v-spacer />
            </v-chip-group>
          </v-col>

          <!-- Limited Availability Period -->
          <service-manager-conditional-panel
            class="pt-4"
            :value="limitPeriodSet"
            :label="$tc('label.limit_available_period')"
            @input="limitPeriodChanged"
          >
            <v-col
              class="py-0 my-0"
              cols="12"
              lg="6"
            >
              <validation-provider
                v-slot="{ errors }"
                name="vpDateFrom"
                rules="required|lteqDate:@vpDateUntil"
              >
                <date-picker-field
                  v-model="service.localStartDate"
                  :format-fn="fmtShortDate"
                  :label="$t('label.service_available_from')"
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
            <!-- -->
            <v-col
              class="py-0 my-0"
              cols="12"
              lg="6"
            >
              <validation-provider
                v-slot="{ errors }"
                name="vpDateUntil"
                rules="required|gteqDate:@vpDateFrom"
              >
                <date-picker-field
                  v-model="service.localEndDate"
                  :format-fn="fmtShortDate"
                  :label="$t('label.service_available_until')"
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
          </service-manager-conditional-panel>

          <!-- Blackout -->
          <service-manager-conditional-panel
            :value="blackoutSet"
            :label="$tc('label.set_blackout_period')"
            @input="blackoutSetChanged"
          >
            <template v-for="b, i in service.blackouts">
              <v-col
                :key="`blackout_from_${i}`"
                class="py-0 my-0"
                cols="12"
                lg="6"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :name="`vpBlackoutFrom_${i}`"
                  :rules="`required|lteqDate:@vpBlackoutUntil_${i}`"
                >
                  <date-picker-field
                    :value="service.blackouts[i].blackoutStartDate"
                    :label="$t('label.disable_service_from')"
                    :format-fn="fmtShortDate"
                    :error-messages="errors"
                    @input="blackoutStartDateChange(i, $event)"
                  />
                </validation-provider>
              </v-col>
              <v-col
                :key="`blackout_until_${i}`"
                class="py-0 my-0"
                cols="12"
                lg="6"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :name="`vpBlackoutUntil_${i}`"
                  :rules="`required|gteqDate:@vpBlackoutFrom_${i}`"
                >
                  <date-picker-field
                    :value="service.blackouts[i].blackoutEndDate"
                    :label="$t('label.disable_service_until')"
                    :format-fn="fmtShortDate"
                    :error-messages="errors"
                    :append-outer-icon="i === service.blackouts.length -1 ? 'mdi-plus' : 'mdi-close'"
                    :min-date="b.blackoutStartDate"
                    @click:append-outer="i === service.blackouts.length - 1 ? addBlackoutRow() : deleteBlackoutRow(i)"
                    @input="blackoutEndDateChange(i, $event)"
                  />
                </validation-provider>
              </v-col>
            </template>
          </service-manager-conditional-panel>
        </settings-group>

        <settings-group
          :title="$t('settings.service_manager.payments_title')"
          :subtitle="$t('settings.service_manager.payments_subtitle')"
        >
          <v-alert
            v-if="!installedPaymentProvider"
            type="warning"
            dense
            prominent
          >
            <v-row align="center">
              <v-col class="grow">
                {{ $t('label.service_manager_payment_processor_not_active') }}
              </v-col>
              <v-col class="shrink">
                <v-btn @click="configurePayments">
                  {{ $t('button.configure_now') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>
          <!-- Payment-->
          <service-manager-conditional-panel
            v-else
            :value="stripeSet"
            :label="$tc('label.service_enable_payments')"
            @input="stripeSetChanged"
            @click="focusPrice=true"
          >
            <v-col
              sm="3"
              cols="6"
              class="mt-2"
            >
              <amount-field
                v-model="service.price"
                :autofocus="focusPrice"
              />
            </v-col>
            <v-col
              sm="2"
              cols="6"
              class="py-0 my-0 mt-2"
            >
              <v-select
                v-model="service.currency"
                :label="$t('label.currency')"
                return-object
                :items="currencies"
              />
            </v-col>
            <v-col
              cols="12"
              class="my-0 py-0"
            >
              <v-switch
                v-model="service.isPricePerPerson"
                class="mt-2"
                :label="$t('label.price_per_person')"
              />
            </v-col>
            <v-col
              cols="12"
              class="my-0 py-0"
            >
              <v-switch
                v-model="service.isDeposit"
                class="mt-2"
                :label="$t('label.price_is_deposit')"
              />
            </v-col>
          </service-manager-conditional-panel>
        </settings-group>

        <settings-group
          :title="$t('settings.service_manager.advanced_settings_title')"
          :subtitle="$t('settings.service_manager.advanced_settings_subtitle')"
          gutter
          :gutter-md="0"
          :gutter-xl="6"
          :gutter-lg="6"
        >
          <v-col
            cols="12"
            class="py-0"
          >
            <section-header :title="$t('label.party_size')" />
          </v-col>

          <v-col
            cols="6"
            class="py-0"
          >
            <validation-provider
              v-slot="{ errors }"
              name="vpMinParty"
              :rules="`lteq:@vpMaxParty${service.maxPartySize ? '|required' : ''}`"
            >
              <v-select
                v-model="service.minPartySize"
                :items="partysizes()"
                :label="$t('label.minimum_short')"
                :error-messages="errors"
                :placeholder="$t('label.default_value')"
                persistent-placeholder
                :append-outer-icon="clearButton(service.minPartySize)"
                @click:append-outer="service.minPartySize=undefined"
              />
            </validation-provider>
          </v-col>

          <v-col
            cols="6"
            class="py-0"
          >
            <validation-provider
              v-slot="{ errors }"
              name="vpMaxParty"
              :rules="`gteq:@vpMinParty${service.minPartySize ? '|required' : ''}`"
            >
              <v-select
                v-model="service.maxPartySize"
                :items="partysizes()"
                :label="$t('label.maximum_short')"
                :error-messages="errors"
                :placeholder="$t('label.default_value')"
                persistent-placeholder
                :append-outer-icon="clearButton(service.maxPartySize)"
                @click:append-outer="service.maxPartySize=undefined"
              />
            </validation-provider>
          </v-col>
          <v-col
            cols="12"
            class="py-0"
          >
            <!-- @vue-expect-error -->
            <v-select
              v-model="service.tabs"
              item-value="id"
              item-text="name"
              :item-disabled="(item)=>!item.usingWeekdaysAndTimes"
              :items="tabs"
              multiple
              :placeholder="$t('label.any_available')"
              persistent-placeholder
              return-object
              :label="$t('label.section')"
              :menu-props="{offsetY: true}"
              :append-outer-icon="clearButton(service.tabs.length > 0)"
              @click:append-outer="service.tabs=[]"
              @input="serviceTabsChanged"
            >
              <template #selection="{ item, index }">
                <span :class="[ {'text--disabled':!item.usingWeekdaysAndTimes}]">{{ item.name }}</span>
                <v-chip
                  v-if="!item.usingWeekdaysAndTimes"
                  color="grey"
                  x-small
                >
                  <span>{{ $t('label.offline') }}</span>
                </v-chip>
                <span v-if="index < service.tabs.length - 1">,&ensp;</span>
              </template>
              <template #item="{item}">
                <v-checkbox
                  class="pt-0 mt-0"
                  :input-value="service.tabs.includes(item)"
                  :disabled="!item.usingWeekdaysAndTimes"
                  dense
                  hide-details
                />
                <span :class="['servicemgr-v-multiselect d-block grow', {'text--disabled':!item.usingWeekdaysAndTimes} ]">{{ item.name }}</span>
                <v-chip
                  v-if="!item.usingWeekdaysAndTimes"
                  color="grey"
                  x-small
                >
                  <span>{{ $t('label.offline') }}</span>
                </v-chip>
              </template>
            </v-select>
          </v-col>
          <v-col
            cols="6"
            class="pt-0"
          >
            <v-select
              v-model="service.minMinutesInAdvance"
              :items="useTimeInAdvanceItems().value"
              :label="$t('label.min_minutes_prior_to_arrival_time')"
              :placeholder="$t('label.default_value')"
              persistent-placeholder
              :append-outer-icon="clearButton(service.minMinutesInAdvance)"
              persistent-hint
              :hint="$t('label.min_minutes_prior_to_arrival_time_hint')"
              @click:append-outer="service.minMinutesInAdvance=undefined"
            />
          </v-col>
          <v-col
            cols="6"
            class="pt-0"
          >
            <v-select
              v-model="service.minMinutesToModify"
              :items="useTimeInAdvanceItems().value"
              :label="$t('label.modify_cancel_in_advance')"
              :placeholder="$t('label.default_value')"
              persistent-hint
              persistent-placeholder
              :hint="$t('label.modify_cancel_in_advance_hint')"
              :append-outer-icon="clearButton(service.minMinutesToModify)"
              @click:append-outer="service.minMinutesToModify=undefined"
            />
          </v-col>

          <!-- Fixed Duration -->
          <service-manager-conditional-panel
            :value="fixedDurationSet"
            :label="$tc('label.set_fixed_duration')"
            @input="fixedDurationSetChanged"
          >
            <v-col
              cols="6"
              class="py-0 my-0"
            >
              <v-select
                v-model="service.duration"
                :items="useSeatingTimeItems().value"
                placeholder="Default"
                persistent-placeholder
                :label="$t('label.duration')"
                :append-outer-icon="clearButton(service.duration)"
                @click:append-outer="service.duration = undefined"
              />
            </v-col>
          </service-manager-conditional-panel>
          <!-- Limit Parallel Bookings (Disabled for now) -->
          <service-manager-conditional-panel
            v-if="false"
            :value="limitParallelSet"
            :label="$tc('label.limit_parallel_bookings')"
            @input="limitParallelSetChanged"
          >
            <v-col
              cols="6"
              class="py-0 my-0"
            >
              <v-select
                v-model="service.maxParallelBookings"
                :label="$t('label.max_number_of_parallel_bookings')"
                :items="limitParallelItems"
              />
            </v-col>
          </service-manager-conditional-panel>
        </settings-group>
      </validation-observer>

      <v-row v-if="!!widgetLink">
        <v-col class="col-12 col-lg-6 offset-0 offset-lg-3 mt-2">
          <v-sheet
            :color="$vuetify.theme.dark ? 'secondary lighten-2' : 'grey lighten-4'"
            class="text-center pa-2"
          >
            <a
              :href="widgetLink"
              target="_blank"
            >{{ widgetLink }}</a>
            <v-icon
              small
              class="pl-1 pb-1"
            >
              mdi-open-in-new
            </v-icon>
            <p class="subheading pa-0 ma-0">
              {{ $t('label.service_direct_link_description') }}
            </p>
          </v-sheet>
        </v-col>
      </v-row>
    </template>
  </settings-form>
</template>

<script setup lang="ts">
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import showConfirmationDialog from '@/components/dialogs/confirmation-dialog';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import i18n from '@/plugins/i18n';
import {
  computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import RichTextEditor from '@/components/editors/RichTextEditor.vue';
import ImageUploadDialog from '@/components/dialogs/ImageUploadDialog.vue';
import localized from '@/plugins/vue-localized-formatter/src/localized';
import {
  useSeatingTimeItems, useTimeInAdvanceItems, partysizes, useSortedLanguages,
  useAccountLocale,
  usePreferredGuestCurrency,
} from '@/components/views/settings/helpers/settings-values';
import ReservationService from '@/grpc-api/model/reservation-service';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import ServiceManagerStore from '@/store/service-manager-store';
import { actionShowError, performDispatchAction, performSaveAction } from '@/services/vue-utils';
import {
  cloneModel, isModelEqualToModel, isModelMapEqualToModelMap, toMapByField,
} from '@/model/model-utils';
import { hostedWidgetURL } from '@/services/configuration';
import useUnsavedGuard from '@/composables/unsaved-guard';
import store from '@/store/store';
import { useRouter } from 'vue-router/composables';
import { ValidationObserver } from 'vee-validate';
import { dateFlooredToDate } from '@/services/time-utils';
import SettingsGroup from '@/components/layouts/SettingsGroup.vue';
import AmountField from '@/components/forms/AmountField.vue';
import GstplnApp from '@/components/apps/app';
import { fullSwatchPalette } from '@/components/helpers/colors';
import httpClient from '@/api/http-client';
import ImagePreviewField from '@/components/forms/ImagePreviewField.vue';
import toast from '@/plugins/toast-plugin/toast';
import copy from 'copy-to-clipboard';
import Tab from '@/model/Tab';
import MultiLanguageGroup from '@/components/dialogs/MultiLanguageGroup.vue';
import ReservationServiceTranslation from '@/grpc-api/model/reservation-service-translation';
import ServiceMergeDialog from './ServiceMergeDialog.vue';
import ServiceManagerConditionalPanel from './ServiceManagerConditionalPanel.vue';

const IS_SVCMGR_INTERNAL_BUILD = false;
const focusPrice = ref(false);

const router = useRouter();

let oldService = new ReservationService();
let oldTranslations = [] as ReservationServiceTranslation[];
const service = ref(cloneModel(oldService));
const translations = ref(oldTranslations.map(((t) => t.clone())));

// Languages
const languages = useSortedLanguages();
const selectedLocale = ref('en');
const accountLocale = computed(() => store.getters.accountLocale as string);
const enabledLocales = computed(() => {
  const localesSet = new Set<string>(store.getters.enabledLocales);
  const translatedSet = new Set(translations.value.map((t) => t.locale));
  const locales = languages.value.filter((l) => localesSet.has(l.locale) || translatedSet.has(l.locale));
  return locales;
});
const translatedLocales = computed(() => translations.value.map((t) => t.locale));
// const languageDisplayName = computed(() => languages.value.find((l) => l.locale === selectedLocale.value)?.language);

// add and delete translation
const addTranslation = (locale: string) => {
  if (!translations.value.some((t) => t.locale === locale)) {
    const newTranslation = new ReservationServiceTranslation();
    newTranslation.locale = locale;
    newTranslation.imageUrl = translations.value[0].imageUrl || '';
    translations.value.push(newTranslation);
  }
  selectedLocale.value = locale;
};

const deleteTranslation = (locale: string) => {
  if (locale === accountLocale.value) return;
  translations.value = translations.value.filter((t) => t.locale !== locale);
  if (selectedLocale.value === locale) selectedLocale.value = accountLocale.value;
};

// Translation Fields
const getTranslation = () => translations.value.find((t) => t.locale === selectedLocale.value);
const serviceTitle = computed({
  get: () => getTranslation()?.title || '',
  set: (val: string) => { getTranslation()!.title = val; },
});

const serviceDescription = computed({
  get: () => getTranslation()?.description || '',
  set: (val: string) => { getTranslation()!.description = val; },
});

const serviceImageUrl = computed({
  get: () => getTranslation()?.imageUrl || '',
  set: (val: string) => { getTranslation()!.imageUrl = val; },
});

// Service Tags
const serviceTags = ref(['', '', '']);
watch(selectedLocale, () => { serviceTags.value = getTranslation()!.tags; });
watch(serviceTags, (tags) => { getTranslation()!.tags = tags; });

// Image Upload
const uploadDialog = ref(false);

const uploadImage = async (image: File | null) => {
  // service.value.imageUrl = image ? URL.createObjectURL(image as Blob) : '';
  if (image === null) {
    serviceImageUrl.value = '';
    uploadDialog.value = false;
    return;
  }

  const ok = await performSaveAction(
    null,
    async () => {
      const key = store.state.settings.account.serviceManagerAccessKey ?? '';
      const imageUrl = await httpClient.uploadImageFile(image, key, { createThumbnail: true });
      serviceImageUrl.value = imageUrl;
    },
  );

  if (ok) {
    uploadDialog.value = false;
  }
};

// ################################################################################
// #                                Service Merge                                 #
// ################################################################################

const serviceMergeDialogVisible = ref(false);
const closeServiceMergeDialog = () => {
  // eslint-disable-next-line no-use-before-define
  reset();
  serviceMergeDialogVisible.value = false;
};

// ################################################################################
// #                               / Service Merge                                #
// ################################################################################
//
const weekDaysChanged = (value: number[]) => { value.sort(); service.value.weekDays = value; };

const saved = ref(false);

const isLoaded = computed(() => store.getters.isServiceManagerLoaded as boolean);

const isNew = computed(() => service.value.id <= 0 && isLoaded.value);

const isChanged = computed(
  () => (!isModelEqualToModel(oldService, service.value) || !isModelEqualToModel(oldTranslations, translations.value))
  && !saved.value,
);
useUnsavedGuard(isChanged);

const tabs = computed(() => store.state.settings.tabs
  .filter((t) => t.usingWeekdaysAndTimes || service.value.tabs.some((st) => st.id === t.id)));

const widgetLink = computed(() => {
  const { defaultAccessKey: apiKey } = store.state.settings.account;
  const { code: serviceCode } = service.value;
  if (!apiKey || !serviceCode) return undefined;
  return hostedWidgetURL({ apiKey, serviceCode, locale: selectedLocale.value });
});

const fmtShortDate = (d: Date) => localized.veryShortDateText(d);

const clearButton = (val: any) => (val ? 'mdi-close' : undefined);

const fixedDurationSet = computed(() => !!service.value.duration);
const fixedDurationSetChanged = (val: boolean) => {
  if (!val) service.value.duration = undefined;
  else if (!service.value.duration) {
    const durations = service.value.tabs.map((t) => t.duration ?? 60);
    service.value.duration = Math.min(60, ...durations);
  }
};

const limitPeriodSet = computed(() => !!service.value.localStartDate && !!service.value.localEndDate);
const limitPeriodChanged = (val: boolean) => {
  if (!val) {
    service.value.localStartDate = undefined;
    service.value.localEndDate = undefined;
  } else {
    if (service.value.localStartDate === undefined) service.value.localStartDate = dateFlooredToDate(new Date());
    if (service.value.localEndDate === undefined) service.value.localEndDate = dateFlooredToDate(new Date());
  }
};

const blackoutSet = computed(() => service.value.blackouts.length > 0);
const blackoutSetChanged = (val: boolean) => {
  if (!val) {
    service.value.blackouts = [];
  } else {
    service.value.blackouts = [
      {
        blackoutStartDate: dateFlooredToDate(new Date()),
        blackoutEndDate: dateFlooredToDate(new Date()),
      },
    ];
  }
};

const blackoutStartDateChange = (i: number, date: Date) => {
  const blackout = { ...service.value.blackouts[i] };
  blackout.blackoutStartDate = date;
  service.value.blackouts.splice(i, 1, blackout);
};

const blackoutEndDateChange = (i: number, date: Date) => {
  const blackout = { ...service.value.blackouts[i] };
  blackout.blackoutEndDate = date;
  service.value.blackouts.splice(i, 1, blackout);
};

const delayedBlur = () => {
  setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 100);
};

const addBlackoutRow = () => {
  delayedBlur();
  service.value.blackouts.push({
    blackoutStartDate: dateFlooredToDate(new Date()),
    blackoutEndDate: dateFlooredToDate(new Date()),
  });
};

const deleteBlackoutRow = (idx: number) => {
  delayedBlur();
  service.value.blackouts.splice(idx, 1);
};

const currencies = computed(() => store.state.serviceManager.currencies);

// eslint-disable-next-line vue/max-len
const installedPaymentProvider = computed(() => ['mollie-connect', 'stripe-connect', 'stripe'].find((appName) => store.getters.gstplnApps.find((a: GstplnApp) => a.apiName === `app.${appName}`)?.installed));

// eslint-disable-next-line vue/max-len
const configurePayments = () => router.replace(`/apps/manage/?category=payments${installedPaymentProvider.value ? `&configure=${installedPaymentProvider.value}` : ''}`);

const stripeSet = computed(() => !!service.value.price || !!service.value.currency);
const stripeSetChanged = (val: boolean) => {
  focusPrice.value = val;
  if (!val) {
    service.value.price = undefined;
    service.value.isPricePerPerson = undefined;
    service.value.currency = undefined;
  } else {
    if (service.value.isPricePerPerson === undefined) service.value.isPricePerPerson = false;

    const curr = usePreferredGuestCurrency().value;
    console.log(curr);
    console.log(currencies.value);
    if (service.value.currency === undefined) {
      service.value.currency = currencies.value.find((c) => c.code === curr?.toUpperCase());
    }
  }
};

// section multiselect
const serviceTabsChanged = (value: Tab[]) => {
  service.value.tabs = Array.from(value);
  service.value.tabs.sort((t1, t2) => t1.id - t2.id);
};

const limitParallelSet = computed(() => !!service.value.maxParallelBookings);
const limitParallelSetChanged = (val: boolean) => {
  if (!val) {
    service.value.maxParallelBookings = undefined;
  } else if (service.value.maxParallelBookings === undefined) service.value.maxParallelBookings = 4;
};
const limitParallelItems = ref([...new Array(99)].map((e, i) => i + 1));

const reset = async () => {
  const route = router.currentRoute;

  const id = Number(route.params.id) || 0;
  let s = store.state.serviceManager.services.find((o) => o.id === id);

  if (!s) {
    const st = new ReservationServiceTranslation();
    st.locale = accountLocale.value;

    s = new ReservationService();
    s.localStartTime = '06:00';
    s.localEndTime = '23:45';
    s.weekDays = [1, 2, 3, 4, 5, 6, 7];
    s.isActive = true;
    s.isPromoted = true;
    s.isDeposit = true;
    s.translations = [st];

    if (route.params.id !== 'new') {
      actionShowError(new Error(i18n.tc('error.service_not_found')));
      router.replace('/settings/service-manager/new');
    }
  } else if (s.translations.length === 0) {
    const st = new ReservationServiceTranslation();
    st.locale = s.locale || accountLocale.value;
    s = cloneModel(s);

    st.title = s.title || '';
    st.description = s.description || '';
    st.imageUrl = s.imageUrl || '';
    st.tags = s.tags || [];
    s.translations = [st];
  }

  if (s && route.query.action === 'copy') {
    const dup = cloneModel(s);
    dup.id = 0;
    if (dup.title && dup.title.length > 0) dup.title += ` (${i18n.t('label.copy_of')})`;
    /* eslint-disable no-param-reassign */
    dup.translations.forEach((t) => { t.title += ` (${i18n.t('label.copy_of')})`; });

    oldService = new ReservationService();
    service.value = dup;
  } else {
    oldService = cloneModel(s);
    service.value = cloneModel(s);
  }

  // translations
  oldTranslations = oldService.translations;
  translations.value = service.value.translations;

  oldService.translations = [];
  service.value.translations = [];

  // set selected locale to default or first available
  if (translations.value.some((t) => t.locale === accountLocale.value)) selectedLocale.value = accountLocale.value;
  else selectedLocale.value = translations.value[0].locale;

  serviceTags.value = getTranslation()!.tags;
};

const load = async () => {
  if (!store.getters.isServiceManagerLoaded) {
    await performDispatchAction('loadServiceManager', {}, { streamLoad: true }) as ReservationService[] | null;
  }
  reset();
};

const refObserver = ref(null as null | InstanceType<typeof ValidationObserver>);
const tInError = ref(undefined as undefined | string[]);

const validateAllTranslations = () => Array.from(translations.value.values()).map((t) => ((
  t.tags.some((tag) => tag.length > 255)
      || !t.imageUrl
      || !t.title || t.title.length > 255
      || !t.description || t.description.length > 1500
) ? t.locale : undefined)).reduce((acc, locale) => {
  if (locale && locale !== selectedLocale.value) { acc.push(locale); }
  return acc;
}, [] as string[]);

watch(() => refObserver.value?.errors, () => {
  console.log(refObserver.value?.errors);
  tInError.value = validateAllTranslations();
});

const save = async () => {
  const saveService = cloneModel(service.value);
  saveService.weekDays.sort();
  saveService.title.trim();
  saveService.translations = Array.from(translations.value.values());

  tInError.value = validateAllTranslations();
  console.log(tInError.value);
  if (tInError.value.length > 0) {
    toast.error(i18n.tc('error.validation_failed'));
    return;
  }
  const ok = await performSaveAction(
    refObserver.value,
    async () => store.dispatch('serviceManagerUpdateService', { service: saveService }),
  );

  if (!ok) return;
  saved.value = true;
  router.replace('/settings/service-manager');
};

const deleteService = async () => {
  const s = cloneModel(service.value);
  s.isDeleted = true;
  const ok = await performSaveAction(
    undefined,
    async () => store.dispatch('serviceManagerUpdateService', { service: s }),
  );
  if (!ok) return;

  router.replace('/settings/service-manager');
};

const copySvcCode = () => {
  if (!service.value.code) { return; }
  copy(service.value.code);
  toast.success(i18n.t('message.service_id_copied'));
};

const deleteConfirmation = () => {
  showConfirmationDialog(
    i18n.tc('title.service_manager'),
    i18n.tc('message.confirm_delete_service'),
    { title: i18n.tc('button.yes'), action: () => deleteService() },
    { title: i18n.tc('button.cancel') },
  );
};

onBeforeUnmount(() => {
  unloadStoreModule(
    getCurrentInstance()?.proxy!,
    'serviceManager',
    async () => { store.dispatch('resetServiceManager'); },
  );
});

onMounted(() => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'serviceManager', ServiceManagerStore, async () => { load(); });
});

</script>

<style scoped lang="scss">

  .chip-prominent-active {
    background-color: $green-color;
    color: $white-color;
  }

  span.servicemgr-v-multiselect {
    margin-bottom: 3px;
  }

</style>
